import type { Ref } from "react";
import { Fireworks, FireworksOptions, type FireworksHandlers } from "@fireworks-js/react";
import { FIREWORKS_OPTIONS } from "../data/animations/fireworks";
import shared from "../data/js/shared";

// Import subcomponents
import {
	ErrorMessage,
	SocialLoginButtons,
	OTPSection,
	LoginHeader,
	LoginNavigation,
	SuccessScreens,
	NameInput,
	EmailInput,
	useLoginFlow,
} from "./login/index";

import DarkModeLabel from "./dark-mode-label";
import Logo from "./logo";
import Palms from "./palms";
import { PhoneInput } from "./single-inputs";
import ToggleSwitch from "./toggle-switch";

const {
	phoneLabel,
	phoneEmailLabel,
	phoneEmptyErrorMessage,
	phoneInvalidErrorMessage,
	continueButtonLabel,
	verifyButtonLabel,
	verifyingButtonLabel,
	submittingLabel,
	tagLine,
} = shared.login;

export interface LoginProps {
	close?: boolean;
}

/**
 * Login Component
 *
 * Main authentication component handling multiple login flows:
 * - Passwordless login with OTP
 * - Social login (Google, Facebook, Apple)
 * - Profile completion
 */
const Login = ({ close: _close = false }: LoginProps) => {
	const {
		// State
		loginView,
		submitting,
		fields,
		errors,
		focus,
		filled,
		otpCode,
		otpError,
		theme,
		themeHydrated,
		emailAddtl,

		// Setters
		setFields,
		setErrors,
		setFocus,
		setFilled,
		setOTPCode,
		updateField,
		setFieldFocus,
		setPopupContents,
		setPopupOpen,

		// Handlers
		handleSubmit,
		resendCode,
		closeLogin,
		handleGoogleLogin,
		loginWithFacebook,
		loginWithApple,
		toggleTheme,
		checkPhone,

		// Ref
		fireworksRef,
	} = useLoginFlow();

	return (
		<div className={`login-flow STATE--${loginView} ${emailAddtl}`}>
			<div className="fireworks-bg">
				<Fireworks
					ref={fireworksRef as unknown as Ref<FireworksHandlers>}
					options={FIREWORKS_OPTIONS as FireworksOptions}
					className="top-0 left-0 w-screen h-screen fixed bg-transparent z-0"
				/>
			</div>

			<div id="google-popup-cont" />

			<div className="login-left login-side login-palms">
				<Logo className="desktop-switcheroo" />
				<h2 className="sellwild-tagline">{tagLine}</h2>
				<Palms />
			</div>

			<div className="login-right login-side">
				<LoginHeader loginView={loginView} />

				<form
					method="POST"
					action="/"
					className="login-form"
					onSubmit={handleSubmit}
				>
					<SocialLoginButtons
						onGoogleLogin={handleGoogleLogin}
						onFacebookLogin={loginWithFacebook}
						onAppleLogin={loginWithApple}
					/>

					<OTPSection
						otpValue={otpCode}
						onOtpChange={setOTPCode}
						error={otpError}
					/>

					<NameInput
						value={fields.name}
						error={errors.name}
						focus={focus.name || false}
						onChange={(value) => updateField('name', value)}
						onFocus={() => setFieldFocus('name', true)}
						onBlur={() => setFieldFocus('name', false)}
					/>

					<EmailInput
						optional={loginView === "more-info"}
						value={fields.email}
						error={errors.email}
						focus={focus.email || false}
						onChange={(value) => updateField('email', value)}
						onFocus={() => setFieldFocus('email', true)}
						onBlur={() => setFieldFocus('email', false)}
					/>


					<PhoneInput
						id="phone"
						realName="phone"
						setFocus={setFocus}
						onFocus={setFocus}
						validate={checkPhone}
						setState={setFields}
						label={phoneEmailLabel}
						phoneLabel={phoneLabel}
						state={fields}
						errors={errors}
						focus={focus}
						filled={filled}
						setFilled={setFilled}
						setErrors={setErrors}
						setPopupContents={setPopupContents}
						setPopupOpen={setPopupOpen}
						errorMessages={
							<>
								<ErrorMessage
									cls="phone-short-error"
									msg={phoneEmptyErrorMessage}
								/>
								<ErrorMessage
									cls="phone-invalid-error"
									msg={phoneInvalidErrorMessage}
								/>
							</>
						}
					/>

					<button className="verify-button" data-testid="verify-otp-button" type="submit">
						{submitting ? verifyingButtonLabel : verifyButtonLabel}
					</button>
					<button className="login-button" data-testid="continue-button" type="submit">
						{submitting ? submittingLabel : continueButtonLabel}
					</button>

					<SuccessScreens onProceed={closeLogin} onResend={resendCode} />

				</form>

				<LoginNavigation loginView={loginView} />

				<div className="theme-option">
					<ToggleSwitch
						checked={theme === "dark"}
						onClick={toggleTheme}
					/>
					<DarkModeLabel theme={theme} hydrated={themeHydrated} />
				</div>
			</div>
		</div>
	);
};

export default Login;
